/* eslint-disable @angular-eslint/prefer-inject --
 * Constructor parameter injection is intentional: it lets specs instantiate
 * this tracker directly with stubs and skip Angular's TestBed boot.
 */
import { Injectable } from '@angular/core';
import { NavigationEnd, Router, type Event as RouterEvent } from '@angular/router';
import { SpanStatusCode, type Tracer } from '@opentelemetry/api';
import { type Subscription, filter, first } from 'rxjs';

import { APP_BOOTSTRAP_PAGE_NAME, SPA_OTEL_ATTR, SPA_PERF_SPAN_PREFIX } from './metrics.types';
import { getMetricsTracer } from './otel-web.bootstrap';

/**
 * Captures a one-time "app bootstrap" PAGE_LOAD span:
 *   start = `performance.timeOrigin` (browser nav start)
 *   end   = first `NavigationEnd` event after start()
 *
 * Why NavigationEnd (and not ApplicationRef.isStable):
 *   See `metrics-router.tracker.ts` for the full rationale. In short:
 *   isStable is unreliable in apps with persistent timers (MSAL, long-poll).
 *   NavigationEnd reliably fires when the initial route + its resolvers are
 *   complete, which is the right "app is bootstrapped" signal.
 *
 * The span uses the sentinel page name `__app:bootstrap__` so monthly
 * aggregations can include or exclude cold-start times deliberately.
 *
 * The tenant id may not be known at bootstrap time (the SPA fetches branding
 * during init); the tracker captures it lazily right before the span ends.
 * If no tenant id is available, the processor drops the span (acceptable
 * one-shot loss on cold start before login).
 */
@Injectable({ providedIn: 'root' })
export class AppBootstrapTracker {
  private static readonly SAFETY_TIMEOUT_MS = 30_000;
  private static readonly SPAN_NAME = `${SPA_PERF_SPAN_PREFIX}page_load`;

  private fired = false;
  private tracerOverride: Tracer | null = null;
  private tenantIdProvider: () => string | null = () => sessionStorage.getItem('tenantId');
  private routerSub: Subscription | null = null;

  constructor(private readonly router: Router) {}

  public configureForTest(opts: { tracer?: Tracer; tenantIdProvider?: () => string | null }): void {
    if (opts.tracer) {
      this.tracerOverride = opts.tracer;
    }
    if (opts.tenantIdProvider) {
      this.tenantIdProvider = opts.tenantIdProvider;
    }
  }

  public start(): void {
    if (this.fired) {
      return;
    }
    this.fired = true;

    const tracer = this.tracerOverride ?? getMetricsTracer();
    const startMs = this.bootstrapStartMs();
    const span = tracer.startSpan(AppBootstrapTracker.SPAN_NAME, {
      startTime: startMs,
      attributes: {
        [SPA_OTEL_ATTR.PAGE_NAME]: APP_BOOTSTRAP_PAGE_NAME,
        [SPA_OTEL_ATTR.EVENT_TYPE]: 'PAGE_LOAD',
      },
    });

    // Safety timer: if NavigationEnd never fires (e.g. router stuck in a guard
    // redirect loop), drop the span so it doesn't leak.
    let timer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      span.setStatus({ code: SpanStatusCode.ERROR, message: 'navigation_end_timeout' });
      span.end();
      timer = null;
      this.routerSub?.unsubscribe();
      this.routerSub = null;
    }, AppBootstrapTracker.SAFETY_TIMEOUT_MS);

    this.routerSub = this.router.events
      .pipe(
        filter((e: RouterEvent): e is NavigationEnd => e instanceof NavigationEnd),
        first()
      )
      .subscribe(() => {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
        const tenantId = this.tenantIdProvider();
        if (tenantId) {
          span.setAttribute(SPA_OTEL_ATTR.TENANT_ID, tenantId);
        }
        span.end();
        this.routerSub = null;
      });
  }

  private bootstrapStartMs(): number {
    if (typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number') {
      return performance.timeOrigin;
    }
    return Date.now();
  }
}
